// Errors and the body seam for the `/v1` client (`lib/api.ts`), split out the
// same way `paged-types.ts` was — to keep both files under the dashboard's
// 150-LOC cap. `api.ts` re-exports every public name here, so callers keep
// importing one module and never need to know about this split.
//
// One reason to change: what a failed call IS. `api.ts` next door owns how a
// request is made.

import type { ZodType } from 'zod';

/** A non-2xx response. Carries the status so callers can branch (401 → re-auth). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Client-minted correlation id sent with the request (see `apiFetch`). */
    readonly requestId?: string,
    /** Human reason from an RFC 9457 problem body, when the API sent one. */
    readonly detail?: string,
    /**
     * The machine half of that body: its RFC 9457 extension members
     * (`retry_after` on a 429, `capability` on a 403, `sink` on a 501), read by
     * `problemExtensions`. `undefined` unless the body carried at least one.
     */
    readonly extensions?: Readonly<Record<string, unknown>>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * A 2xx whose BODY is not the shape this client asked for — the failure the
 * door could not previously report at all, because `apiFetch` ended in
 * `(await res.json()) as T`. Nothing mechanical kept a hand-written mirror equal
 * to the projection it mirrors, so a drifted field arrived as `undefined` and
 * every compiler in the repo stayed green over it (measured: the hand-written
 * `RunnerRow` vs the route's `RunnerRosterResource`, three fields the server
 * sent and the client never declared).
 *
 * It is an `ApiError` so every existing catch keeps working, and it carries the
 * response's REAL status — so `isUnauthorized` still answers about the session
 * rather than about the body, and a caller may branch on `instanceof` to tell
 * "the server refused" from "the server answered something we cannot read".
 *
 * `detail` is deliberately LEFT UNSET. That field is the sentence a UI renders
 * (`errorReason`) and the API's own `detail` is a translated-by-authorship
 * human reason; this refusal is client-minted English about a field path, so
 * putting it there would print `data.0.health: Invalid input` at a maintainer.
 * The diagnosis rides `message` + {@link issues}, which is what the `ApiError`
 * docblock already calls wire debug, and the screen keeps its own `t()`
 * fallback.
 */
export class ApiSchemaError extends ApiError {
  constructor(
    status: number,
    /** `"<METHOD> <path>"` — the seam, same spelling as `ApiError.message`. */
    seam: string,
    requestId: string | undefined,
    /** One `"<path>: <message>"` per rejected field, bounded (see `describeIssues`). */
    readonly issues: readonly string[],
  ) {
    super(status, `${seam} → unexpected body: ${issues.join('; ')}`, requestId);
    this.name = 'ApiSchemaError';
  }
}

/** At most this many field errors reach the message — a wholesale mismatch is not a log. */
const MAX_REPORTED_ISSUES = 5;

/** The slice of a Zod issue this formatter reads; `errors` is an `invalid_union`'s per-arm issues. */
type IssueLike = {
  readonly path: readonly PropertyKey[];
  readonly message: string;
  readonly errors?: readonly (readonly IssueLike[])[];
};

/**
 * Leaf issues, a union's arms descended. A union fails as ONE `invalid_union`
 * issue whose message is a bare "Invalid input" naming no field; its arms'
 * issues ride in `errors`, each path relative to the union's own.
 */
function leafIssues(issues: readonly IssueLike[], prefix: readonly PropertyKey[]): IssueLike[] {
  return issues.flatMap((i) => {
    const path = [...prefix, ...i.path];
    return i.errors !== undefined && i.errors.length > 0
      ? i.errors.flatMap((arm) => leafIssues(arm, path))
      : [{ path, message: i.message }];
  });
}

/** Zod issues as `"<path>: <message>"`, bounded. `path` empty ⇒ the body itself. */
function describeIssues(error: { issues: readonly IssueLike[] }) {
  return leafIssues(error.issues, [])
    .slice(0, MAX_REPORTED_ISSUES)
    .map((i) => `${i.path.length > 0 ? i.path.join('.') : '(body)'}: ${i.message}`);
}

/**
 * The one status that means "the credential is gone" — not "you may not do
 * this" (403) and not "the server is unwell" (5xx). Every 401 branch in the SPA
 * asks this rather than re-testing `instanceof` + `.status` (see
 * `lib/session-expiry.ts` for why 401 is unambiguous).
 */
export function isUnauthorized(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

/**
 * The one status that means "the credential is fine; THIS CALLER MAY NOT" — a
 * missing scope, or an `X-Dz-Account` tenant claim the membership fence refused
 * (`apps/api/src/middleware/auth.ts`). The sibling of {@link isUnauthorized},
 * and the distinction is the whole point of having two: a 401 is "sign in",
 * which is actionable, so it keeps its affordance; a 403 is an ANSWER — the
 * second call carries the same scopes and the same claim and is refused
 * identically — so it must render with NO Retry.
 *
 * This is the query-side spelling of the rule the stream views already take for
 * a 404 (`isAnsweredRefusal`, `components/RunStreamRefusal.tsx`): an answer is
 * reported as itself, a fault keeps its retry.
 */
export function isForbidden(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403;
}

/**
 * A failure whose `message` was WRITTEN FOR A HUMAN — a localized `t()` sentence
 * the app itself throws (a client-side refusal like a taken board name, or
 * `HeadRefusal` in `pr-detail.ts`). The one non-`ApiError` whose message
 * {@link errorReason} shows: every other `Error` reaching a mutation is the
 * runtime's (`TypeError: Failed to fetch`, a JSON parser's `SyntaxError`) —
 * browser-specific, English, and meaningless on a form.
 */
export class UserFacingError extends Error {}

/**
 * The human reason behind a failed call, for a UI that shows one inline.
 *
 * `ApiError.message` is wire debug — `"POST /v1/org/members → 404"` — and is
 * useless to a maintainer staring at a form. The API already answers RFC 9457
 * problem+json whose `detail` says what actually went wrong ("no registered
 * user with that login"), so prefer it; a {@link UserFacingError} speaks its own
 * sentence; anything else is the caller's generic string — a dead network once
 * toasted "Failed to fetch" on every mutation surface (#5608 slice 17 row 18).
 * Every inline mutation-error surface routes through here so no route has to
 * remember the precedence (#916).
 */
export function errorReason(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.detail ?? fallback;
  if (error instanceof UserFacingError && error.message) return error.message;
  return fallback;
}

/**
 * The seam where a response body stops being `unknown`.
 *
 * With no `schema` it is the old cast, unchanged — which is why this rolls out
 * ENDPOINT BY ENDPOINT rather than as a blanket `.parse()` on all 172 call
 * sites. Today a drifted field renders `undefined`; a blanket parse would turn
 * every one of those into a thrown view on a page that used to work, and a
 * refusal that blanks a screen is a worse answer than a blank cell.
 *
 * With one, the schema must be NON-STRICT (a plain `z.object`, which ignores
 * unknown keys) so the direction that is a normal `/v1` change — the server
 * gaining a field — can never break a bundle already sitting in a browser. Only
 * the other direction throws: a field that vanished, changed type, or went
 * null. What a customer then sees is the screen's OWN error state with its
 * Retry, the same one a 500 produces, rather than a row of blank cells nobody
 * can diagnose from a screenshot.
 */
export function parseBody(
  body: unknown,
  path: string,
  init: RequestInit | undefined,
  requestId: string | undefined,
  opts: { readonly schema: ZodType } | undefined,
  status = 200,
): unknown {
  if (opts === undefined) return body;
  const parsed = opts.schema.safeParse(body);
  if (parsed.success) return parsed.data;
  throw new ApiSchemaError(
    status,
    `${init?.method ?? 'GET'} ${path}`,
    requestId,
    describeIssues(parsed.error),
  );
}
